import firebase from "../firebaseConfig";

const formatLocalDateKey = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Service pour gérer les données FlowTracker dans une collection séparée
 */
const flowTrackerService = {
  /**
   * Récupère les données FlowTracker pour un utilisateur et une date spécifique
   * @param {string} userId - ID de l'utilisateur
   * @param {Date} date - Date pour laquelle récupérer les données
   * @returns {Object} - Données FlowTracker pour la date spécifiée
   */
  async getFlowTrackerData(userId, date) {
    if (!userId || !date) return null;

    const dateString = formatLocalDateKey(date);
    
    try {
      const docRef = firebase.firestore()
        .collection('flowTrackerData')
        .doc(userId)
        .collection('entries')
        .doc(dateString);
      
      const doc = await docRef.get();
      
      if (doc.exists) {
        return doc.data();
      } else {
        // Retourne un objet vide avec les champs obligatoires
        return {
          date: date,
          grandTotal: "00:00",
          meanFlowQuality: "0.00"
        };
      }
    } catch (error) {
      console.error("Error fetching flow tracker data:", error);
      throw error;
    }
  },

  /**
   * Sauvegarde les données FlowTracker pour un utilisateur et une date spécifique
   * @param {string} userId - ID de l'utilisateur
   * @param {Date} date - Date pour laquelle sauvegarder les données
   * @param {Object} data - Données à sauvegarder
   */
  async saveFlowTrackerData(userId, date, data) {
    if (!userId || !date || !data) return;

    const dateString = formatLocalDateKey(date);
    
    try {
      // Nettoyer les données en supprimant les champs vides
      const cleanedData = this.cleanEmptyFields(data);
      
      // Ajouter les champs obligatoires
      cleanedData.date = firebase.firestore.Timestamp.fromDate(date);
      
      const docRef = firebase.firestore()
        .collection('flowTrackerData')
        .doc(userId)
        .collection('entries')
        .doc(dateString);
      
      await docRef.set(cleanedData);
      
      // Mettre à jour le document utilisateur avec une référence à cette entrée
      // (pour la compatibilité avec le code existant)
      const userRef = firebase.firestore().collection('users').doc(userId);
      await userRef.update({
        lastFlowTrackerEntry: dateString
      });
      
      return true;
    } catch (error) {
      console.error("Error saving flow tracker data:", error);
      throw error;
    }
  },
  
  /**
   * Récupère toutes les entrées FlowTracker pour un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @returns {Array} - Tableau de toutes les entrées FlowTracker
   */
  async getAllFlowTrackerEntries(userId) {
    if (!userId) return [];
    
    try {
      const entriesRef = firebase.firestore()
        .collection('flowTrackerData')
        .doc(userId)
        .collection('entries');
      
      const snapshot = await entriesRef.orderBy('date', 'desc').get();
      
      return snapshot.docs.map(doc => doc.data());
    } catch (error) {
      console.error("Error fetching all flow tracker entries:", error);
      throw error;
    }
  },
  
  /**
   * Nettoie les données en supprimant les champs vides
   * @param {Object} data - Données à nettoyer
   * @returns {Object} - Données nettoyées
   */
  cleanEmptyFields(data) {
    const cleanedData = {};

    // Copier uniquement les champs non vides
    Object.keys(data).forEach(key => {
      // Conserver les champs obligatoires même s'ils sont vides
      const requiredFields = ['grandTotal', 'meanFlowQuality'];

      // Champs de temps (ne pas conserver si "00:00" afin d'optimiser la taille)
      const timeFields = [
        'time1_0', 'time1_1', 'time2_0', 'time2_1', 'time3_0', 'time3_1',
        'time4_0', 'time4_1', 'time5_0', 'time5_1', 'time6_0', 'time6_1',
        'time7_0', 'time7_1'
      ];

      // Si c'est un champ obligatoire ou un champ non vide
      if (requiredFields.includes(key) ||
          (timeFields.includes(key) && data[key] && data[key] !== "00:00") ||
          (data[key] && data[key] !== "")) {
        cleanedData[key] = data[key];
      }
    });

    // Nettoyer les extraBlocks
    if (data.extraBlocks && Array.isArray(data.extraBlocks)) {
      cleanedData.extraBlocks = data.extraBlocks.map(block => {
        const cleanedBlock = {};
        Object.keys(block).forEach(key => {
          const value = block[key];
          // Conserver les champs non vides
          if (value !== "" && value !== null && value !== undefined) {
            // Pour les temps, ne pas conserver si "00:00"
            if ((key === 'time0' || key === 'time1') && value === "00:00") {
              return;
            }
            cleanedBlock[key] = value;
          }
        });
        return cleanedBlock;
      }).filter(block => Object.keys(block).length > 0);
    }

    return cleanedData;
  },
  
  /**
   * Migre les données FlowTracker existantes vers la nouvelle structure
   * @param {string} userId - ID de l'utilisateur
   */
  async migrateExistingData(userId, options = {}) {
    if (!userId) return;
    const { deleteOldData = false } = options;
    
    try {
      // Récupérer les données existantes
      const userRef = firebase.firestore().collection('users').doc(userId);
      const userDoc = await userRef.get();
      
      if (!userDoc.exists || !userDoc.data().FlowTrackerData) {
        console.log("No FlowTracker data to migrate");
        return {
          totalLegacyEntries: 0,
          migratedEntries: 0,
          skippedEntries: 0,
          deletedLegacyData: false,
        };
      }
      
      const existingData = userDoc.data().FlowTrackerData;
      let migratedEntries = 0;
      let skippedEntries = 0;
      
      // Migrer chaque entrée vers la nouvelle collection
      for (const entry of existingData) {
        const date = entry?.date?.toDate ? entry.date.toDate() : new Date(entry?.date);
        if (!date || isNaN(date.getTime())) {
          skippedEntries++;
          continue;
        }
        const dateString = formatLocalDateKey(date);
        
        // Nettoyer les données
        const cleanedData = this.cleanEmptyFields(entry?.data || {});
        cleanedData.date = entry?.date?.toDate ? entry.date : firebase.firestore.Timestamp.fromDate(date);
        
        // Sauvegarder dans la nouvelle collection
        const docRef = firebase.firestore()
          .collection('flowTrackerData')
          .doc(userId)
          .collection('entries')
          .doc(dateString);
 
        const existingEntryDoc = await docRef.get();
        if (existingEntryDoc.exists) {
          skippedEntries++;
          continue;
        }
 
        await docRef.set(cleanedData);
        migratedEntries++;
      }
      
      // Mettre à jour le document utilisateur avec une référence à la dernière entrée
      if (existingData.length > 0) {
        const lastEntry = existingData[existingData.length - 1];
        const lastDate = lastEntry?.date?.toDate ? lastEntry.date.toDate() : new Date(lastEntry?.date);
        const lastDateString = formatLocalDateKey(lastDate);
        
        await userRef.update({
          lastFlowTrackerEntry: lastDateString,
          // Optionnel: supprimer les anciennes données après migration
          // FlowTrackerData: firebase.firestore.FieldValue.delete()
        });
      }

      let deletedLegacyData = false;
      if (deleteOldData) {
        await userRef.update({
          FlowTrackerData: firebase.firestore.FieldValue.delete(),
        });
        deletedLegacyData = true;
      }
      
      return {
        totalLegacyEntries: existingData.length,
        migratedEntries,
        skippedEntries,
        deletedLegacyData,
      };
    } catch (error) {
      console.error("Error migrating flow tracker data:", error);
      throw error;
    }
  },

  /**
   * Migre les données FlowTracker de TOUS les utilisateurs (Admin only)
   * @param {Object} options - Options de migration
   * @param {boolean} options.deleteOldData - Supprimer les anciennes données après migration
   * @param {function} options.onProgress - Callback pour suivre la progression
   * @returns {Object} - Statistiques globales de migration
   */
  async migrateAllUsersData(options = {}) {
    const { deleteOldData = false, onProgress = null } = options;

    try {
      // Récupérer tous les utilisateurs
      const usersSnapshot = await firebase.firestore().collection('users').get();
      const users = usersSnapshot.docs;

      const globalStats = {
        totalUsers: users.length,
        usersWithLegacyData: 0,
        usersProcessed: 0,
        totalLegacyEntries: 0,
        totalMigratedEntries: 0,
        totalSkippedEntries: 0,
        errors: []
      };

      for (const userDoc of users) {
        const userId = userDoc.id;
        const userData = userDoc.data();

        // Vérifier si l'utilisateur a des données legacy
        if (userData.FlowTrackerData && userData.FlowTrackerData.length > 0) {
          globalStats.usersWithLegacyData++;

          try {
            const result = await this.migrateExistingData(userId, { deleteOldData });
            globalStats.totalLegacyEntries += result.totalLegacyEntries;
            globalStats.totalMigratedEntries += result.migratedEntries;
            globalStats.totalSkippedEntries += result.skippedEntries;
          } catch (error) {
            globalStats.errors.push({ userId, error: error.message });
          }
        }

        globalStats.usersProcessed++;

        // Callback de progression
        if (onProgress) {
          onProgress({
            current: globalStats.usersProcessed,
            total: globalStats.totalUsers,
            currentUser: userData.username || userData.email || userId
          });
        }
      }

      return globalStats;
    } catch (error) {
      console.error("Error migrating all users data:", error);
      throw error;
    }
  }
};

export default flowTrackerService;
